import React, { useState } from 'react'
import { css } from '@emotion/core'
import { Button, colors, Icon, MessageBox, Spinner, Typography } from '@dw/helix'
import { Form, Formik, FormikActions, FormikProps } from 'formik'
import * as Yup from 'yup'
import { API } from '../../utils/api'
import { InputButton } from '../../components'

const uploadPageStyles = css`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  margin-top: 35px;
`
const panelStyles = css`
  background-color: #fff;
  padding: 0 20px 20px;
  border-radius: 2px;
  box-shadow: 0 1px 7px 0 rgba(0, 0, 0, 0.19), 0 3px 3px 0 rgba(0, 0, 0, 0.13);
  position: relative;
  width: 100%;
  max-width: 600px;
`

const messageStyles = css`
  width: 100%;
  margin-top: 20px;
  margin-bottom: 20px;
`

const listHeadStyles = css`
  display: flex;
  align-items: center;
  margin-top: 20px;
  padding-bottom: 4px;
  border-bottom: 1px solid ${colors.grey4};
`

const iconBinStyles = css`
  height: 14px;
  margin-right: 8px;
  fill: ${colors.brandBlue};
`

const fileListItemStyles = css`
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${colors.grey4};
`

const removeAllButtonStyles = css`
  display: flex;
  margin-left: auto;
  line-height: 15.6px;
  color: currentColor;
  & > svg {
    fill: currentColor;
  }
`

const filesListStyles = css`
  padding: 0;
`

const actionButtonsWrapperStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 40px;

  label:first-of-type {
    margin-bottom: 0;
    display: flex;
    align-items: flex-start;
  }
  button:last-of-type {
    margin-left: 20px;
    display: flex;
    align-items: flex-start;
  }
`

const uploadIconStyles = css`
  height: 14px;
  padding: 1px 0 0;
  margin-right: 8px;
  flex-shrink: 0;
`

const checkmarkIconStyles = css`
  padding: 2px 0 0 0;
  margin-right: 12px;
  flex-shrink: 0;
`

const errorTextStyles = css`
  color: ${colors.support2a};
`

const removeAllStyles = css`
  margin-top: 1px;
`

const CheckmarkIcon = ({ fill, ...props }: any) => (
  <Icon name="checkmark" size="xs" height="auto" fill={fill} css={checkmarkIconStyles} {...props} />
)

export const FILE_UPLOAD_LIMIT = +process.env.REACT_APP_FILE_UPLOAD_FILES_LIMIT!
const MAX_FILE_SIZE = +process.env.REACT_APP_FILE_UPLOAD_MAX_FILE_SIZE!

const SUPPORTED_FILE_TYPES = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']

const UploadFormValidationSchema = Yup.object().shape({
  files: Yup.array()
    .required()
    .of(Yup.mixed().test('files', 'file size to large', file => file.size < MAX_FILE_SIZE))
    .max(FILE_UPLOAD_LIMIT, `You can only upload maximum ${FILE_UPLOAD_LIMIT} files at once`),
})

interface UploadFormValues {
  files: File[]
}
interface FileWithStatus extends File {
  success: boolean
}

const retrieveSignedUrl = (fileName: string, fileContentType: string): Promise<string> => {
  const queryStringParameters = { fileName, fileContentType }

  return API.get('its', '/serials-upload-url', { queryStringParameters })
}

const validateFile = (file: File): string[] => {
  const errors: string[] = []

  if (!/\.(xlsx)$/i.test(file.name) || !SUPPORTED_FILE_TYPES.includes(file.type)) {
    errors.push(
      `The file: ${file.name}, has the wrong file format. Only Excel-files with the file format .xlsx are valid.`,
    )
  }
  if (file.size > MAX_FILE_SIZE) {
    errors.push(
      `The file: ${file.name}, is too large. Only Excel-files less than ${MAX_FILE_SIZE / 1000000} MB are valid.`,
    )
  }
  return errors
}

const handleFileUpload = (file: File): Promise<FileWithStatus> => {
  return retrieveSignedUrl(file.name, file.type)
    .then(signedUrl => {
      const headers = new Headers()
      headers.append('Content-Type', file.type)
      return fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers,
      })
    })
    .then(() => Object.assign(file, { success: true }))
    .catch(() => Object.assign(file, { success: false }))
}

const UploadPage: React.FunctionComponent<{}> = () => {
  const [fileLimitExceeded, setFileLimitExceeded] = useState(false)
  const [processedFilesErrors, setProcessedFilesErrors] = useState<string[]>([])
  const [sentFiles, setSentFiles] = useState<FileWithStatus[]>([])

  const getFileNumber = (fileArray: File[]): string => `${fileArray.length} ${fileArray.length > 1 ? 'files' : 'file'}`

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    formProps: FormikProps<UploadFormValues>,
  ): void => {
    if (!event.target.files) return

    const formFiles = formProps.values.files
    const newFiles = Array.from(event.target.files)

    if (formFiles.length + newFiles.length > FILE_UPLOAD_LIMIT) {
      setFileLimitExceeded(true)
      setProcessedFilesErrors([])
      return
    }

    const newValidFiles = newFiles.filter(file => !validateFile(file).length)
    const errors = newFiles
      .map(file => validateFile(file))
      .reduce((previousValue, currentValue) => previousValue.concat(currentValue), [])

    setFileLimitExceeded(false)
    setProcessedFilesErrors(errors)
    setSentFiles([])

    formProps.setFieldValue('files', formFiles.concat(newValidFiles))
  }

  const handleSubmit = (values: UploadFormValues, actions: FormikActions<UploadFormValues>): void => {
    actions.setSubmitting(true)
    Promise.all(values.files.map(file => handleFileUpload(file)))
      .then((result: FileWithStatus[]) => {
        setSentFiles(result)
        actions.resetForm()
      })
      .finally(() => actions.setSubmitting(false))
  }

  const resetForm = (values: UploadFormValues, actions: FormikActions<UploadFormValues>) => {
    setFileLimitExceeded(false)
    setProcessedFilesErrors([])
    actions.resetForm()
  }

  const filesSent = !!sentFiles.length
  const successfullySent = sentFiles.filter(file => file.success)
  const failedSent = sentFiles.filter(file => !file.success)

  return (
    <Formik
      initialValues={{ files: [] }}
      validationSchema={UploadFormValidationSchema}
      onSubmit={handleSubmit}
      onReset={resetForm}
    >
      {(formProps: FormikProps<UploadFormValues>) => {
        const { values, isSubmitting, isValid } = formProps
        const { files } = values

        return (
          <div css={uploadPageStyles}>
            <div css={panelStyles} className="e2e-test-panel">
              <Typography type="h3">Choose files to upload</Typography>
              <Form>
                {!files.length && !filesSent && (
                  <Typography type="snippet">
                    Please note, only {FILE_UPLOAD_LIMIT} files can be uploaded at once. Only Excel-files with the file
                    format .xslx are valid.
                  </Typography>
                )}
                {filesSent && !isSubmitting && (
                  <div>
                    {successfullySent.length > 0 && (
                      <>
                        <MessageBox
                          status={'success'}
                          css={messageStyles}
                          className="success-message"
                          data-testid="success-message"
                        >
                          {`${getFileNumber(successfullySent)} been received and will now be processed before they get
                          uploaded and searchable in the system. This process will normally take up to an hour. You can
                          leave this page.`}
                        </MessageBox>
                      </>
                    )}
                    {failedSent.length > 0 && (
                      <MessageBox
                        status={'error'}
                        css={messageStyles}
                        className="error-message"
                        data-testid="error-message"
                      >
                        {`${getFileNumber(failedSent)} failed to be uploaded. Please try again.`}
                      </MessageBox>
                    )}
                  </div>
                )}
                {processedFilesErrors.map((error, index) => (
                  <MessageBox
                    key={index}
                    status={'error'}
                    css={messageStyles}
                    className="error-message"
                    data-testid="files-error-message"
                  >
                    {error}
                  </MessageBox>
                ))}
                {fileLimitExceeded && (
                  <MessageBox
                    status={'error'}
                    css={messageStyles}
                    className="error-message"
                    data-testid="limit-error-message"
                  >
                    {`You can only upload maximum ${FILE_UPLOAD_LIMIT} files at once.`}
                  </MessageBox>
                )}
                <div css={listHeadStyles}>
                  <Typography type="h4">File name</Typography>
                  {files.length > 0 && (
                    <Button size="small" theme="secondary" type="reset" css={removeAllButtonStyles}>
                      <Icon name="trash" css={iconBinStyles} />
                      <span css={removeAllStyles}>Remove all</span>
                    </Button>
                  )}
                </div>
                {(files.length > 0 || filesSent) && (
                  <ul css={filesListStyles}>
                    {files.map(file => (
                      <li key={file.name} css={fileListItemStyles}>
                        <Typography type="snippet">{file.name}</Typography>
                      </li>
                    ))}
                    {sentFiles.map(file => (
                      <li key={file.name} css={fileListItemStyles}>
                        <Typography type="snippet">{file.name}</Typography>
                        {file.success ? (
                          <CheckmarkIcon fill={colors.support1a} />
                        ) : (
                          <Typography type="snippet" css={errorTextStyles}>
                            Failed
                          </Typography>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {files.length === 0 && !filesSent && (
                  <Typography
                    type="snippet"
                    css={css`
                      padding: 12px 0;
                      display: block;
                    `}
                    className="e2e-test-no-files"
                  >
                    No files uploaded yet.
                  </Typography>
                )}

                {isSubmitting && (
                  <Spinner
                    color={colors.brandBlue}
                    css={css`
                      padding: 10px;
                    `}
                  />
                )}
                <div css={actionButtonsWrapperStyles}>
                  <Button
                    theme={files.length > 0 ? 'secondary' : 'primary'}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleFileChange(event, formProps)}
                    disabled={isSubmitting}
                    as={(props: any) => <InputButton accept={SUPPORTED_FILE_TYPES.toString()} {...props} />}
                    dataTestid="choose-files-button"
                  >
                    <Icon name="upload" css={uploadIconStyles} />
                    <span>{files.length > 0 ? 'Choose more files' : 'Choose files'}</span>
                  </Button>
                  {files.length > 0 && (
                    <Button type="submit" disabled={!isValid || isSubmitting}>
                      <CheckmarkIcon />
                      <span className="upload-files-button-text">Upload {getFileNumber(files)}</span>
                    </Button>
                  )}
                </div>
              </Form>
            </div>
          </div>
        )
      }}
    </Formik>
  )
}

export default UploadPage
